/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Calendar, Target, Users, BookOpen, Brain, Save, Star, Info, X } from 'lucide-react';

// Helper component for textarea with line numbers
const TextareaWithLineNumbers = ({ value, onChange, placeholder, className, rows = 3, ...props }) => {
  const content = value || '';
  const lines = content.split('\n');
  const lineCount = Math.max(1, lines.length);
  
  // Don't use rows prop for display, only show actual content lines
  const minRows = rows;
  const actualRows = Math.max(minRows, lineCount);
  
  return (
    <div className="glass-textarea-container">
      <div className="glass-textarea-line-numbers">
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i + 1} className="glass-line-number">
            {i + 1}
          </div>
        ))}
      </div>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
        rows={actualRows}
        style={{
          minHeight: `${minRows * 1.5 + 1.5}em`, // Match line-height
          lineHeight: '1.5',
        }}
        {...props}
      />
    </div>
  );
};

const StructuredJournalForms = ({ 
  currentDate, 
  onSave, 
  initialData = null,
  showAIChat,
  handleBusinessCoachToggle 
}) => {
  const [activeTab, setActiveTab] = useState('daily');
  const [isSaving, setIsSaving] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showTabDropdown, setShowTabDropdown] = useState(false);
  const [formData, setFormData] = useState({
    daily: {
      oneThingFocus: '',
      highImpactGoals: ['', '', ''],
      deepWorkBlocks: { morning: '', afternoon: '' },
      quarterlyProgress: ['', '', ''],
      wins: '',
      obstacles: { challenge: '', solution: '' },
      learning: '',
      tomorrowPriorities: ['', '', ''],
      reflection: ''
    },
    meeting: {
      attendees: '',
      agenda: ['', '', '', ''],
      keyDecisions: '',
      actionItems: ['', '', '', '', ''],
      nextSteps: '',
      insights: ''
    },
    project: {
      projectName: '',
      objective: '',
      successDefinition: '',
      leanApproach: { build: '', measure: '', learn: '' },
      timeline: ['', '', '', ''],
      resources: { team: '', budget: '', tools: '' },
      milestones: ['', '', '', ''],
      metrics: { objective: '', kr1: '', kr2: '', kr3: '' },
      risks: ['', '', ''],
      stakeholders: { why: '', how: '', what: '' }
    }
  });

  // Load initial data if editing existing entry
  useEffect(() => {
    if (initialData && initialData.formData) {
      setFormData(initialData.formData);
      setActiveTab(initialData.primaryType || 'daily');
    }
  }, [initialData]);

  const tabs = [
    { 
      id: 'daily', 
      name: 'Focus', 
      icon: <Calendar className="w-4 h-4" />, 
      color: 'from-gray-600 to-gray-700',
      description: 'Execute'
    },
    { 
      id: 'meeting', 
      name: 'Align', 
      icon: <Users className="w-4 h-4" />, 
      color: 'from-gray-500 to-gray-600',
      description: 'Decide'
    },
    { 
      id: 'project', 
      name: 'Build', 
      icon: <Target className="w-4 h-4" />, 
      color: 'from-gray-700 to-gray-800',
      description: 'Launch'
    }
  ];

  const updateFormData = (tab, field, value) => {
    setFormData(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        [field]: value
      }
    }));
  };

  const updateArrayField = (tab, field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        [field]: prev[tab][field].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const updateObjectField = (tab, field, subField, value) => {
    setFormData(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        [field]: {
          ...prev[tab][field],
          [subField]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    const journalEntry = {
      id: Date.now(),
      primaryType: activeTab,
      formData: formData,
      createdAt: new Date().toISOString(),
      date: currentDate,
      title: getEntryTitle(),
      summary: getEntrySummary()
    };

    try {
      await onSave(journalEntry);
      // Reset form after successful save
      setFormData({
        daily: {
          oneThingFocus: '',
          highImpactGoals: ['', '', ''],
          deepWorkBlocks: { morning: '', afternoon: '' },
          quarterlyProgress: ['', '', ''],
          wins: '',
          obstacles: { challenge: '', solution: '' },
          learning: '',
          tomorrowPriorities: ['', '', ''],
          reflection: ''
        },
        meeting: {
          attendees: '',
          agenda: ['', '', '', ''],
          keyDecisions: '',
          actionItems: ['', '', '', '', ''],
          nextSteps: '',
          insights: ''
        },
        project: {
          projectName: '',
          objective: '',
          successDefinition: '',
          leanApproach: { build: '', measure: '', learn: '' },
          timeline: ['', '', '', ''],
          resources: { team: '', budget: '', tools: '' },
          milestones: ['', '', '', ''],
          metrics: { objective: '', kr1: '', kr2: '', kr3: '' },
          risks: ['', '', ''],
          stakeholders: { why: '', how: '', what: '' }
        }
      });
    } catch (error) {
      console.error('Error saving journal entry:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getEntryTitle = () => {
    const data = formData[activeTab];
    switch (activeTab) {
      case 'daily':
        return data.oneThingFocus || `Daily Journal - ${new Date().toLocaleDateString()}`;
      case 'meeting':
        return `Meeting Notes - ${new Date().toLocaleDateString()}`;
      case 'project':
        return data.projectName || `Project Planning - ${new Date().toLocaleDateString()}`;
      default:
        return `Journal Entry - ${new Date().toLocaleDateString()}`;
    }
  };

  const getEntrySummary = () => {
    const data = formData[activeTab];
    switch (activeTab) {
      case 'daily':
        return `Focus: ${data.oneThingFocus.substring(0, 100)}${data.oneThingFocus.length > 100 ? '...' : ''}`;
      case 'meeting':
        return `Attendees: ${data.attendees.substring(0, 100)}${data.attendees.length > 100 ? '...' : ''}`;
      case 'project':
        return `Project: ${data.projectName} - ${data.objective.substring(0, 80)}${data.objective.length > 80 ? '...' : ''}`;
      default:
        return '';
    }
  };

  const isFormValid = () => {
    const data = formData[activeTab];
    switch (activeTab) {
      case 'daily':
        return data.oneThingFocus.trim() !== '' || data.highImpactGoals.some(goal => goal.trim() !== '');
      case 'meeting':
        return data.attendees.trim() !== '' || data.keyDecisions.trim() !== '';
      case 'project':
        return data.projectName.trim() !== '' || data.objective.trim() !== '';
      default:
        return false;
    }
  };

  const renderDailyForm = () => (
    <div className="glass-space-y-6">
      {/* The One Thing */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">THE ONE THING TODAY</h3>
          <p className="glass-section-subtitle">What moves the needle?</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.daily.oneThingFocus}
          onChange={(e) => updateFormData('daily', 'oneThingFocus', e.target.value)}
          placeholder="What is one single action that you can take today that will make the biggest impact to your goal?"
          className="glass-structured-form-textarea"
          rows="3"
        />
      </div>

      {/* High Impact Goals */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">LEVERAGE POINTS</h3>
          <p className="glass-section-subtitle">80/20 Rule</p>
        </div>
        <div className="glass-space-y-2">
          {formData.daily.highImpactGoals.map((goal, index) => (
            <div key={index} className="glass-flex glass-items-center glass-space-x-3">
              <span className="glass-goal-number">{index + 1}.</span>
              <input
                type="text"
                value={goal}
                onChange={(e) => updateArrayField('daily', 'highImpactGoals', index, e.target.value)}
                placeholder="What action will create the most momentum for you this week?"
                className="glass-structured-form-input flex-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Deep Work Blocks */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">DEEP WORK</h3>
          <p className="glass-section-subtitle">Distraction-free blocks</p>
        </div>
        <div className="glass-grid glass-grid-cols-1 glass-md-grid-cols-2">
          <div>
            <label className="glass-form-label">Morning Block</label>
            <input
              type="text"
              value={formData.daily.deepWorkBlocks.morning}
              onChange={(e) => updateObjectField('daily', 'deepWorkBlocks', 'morning', e.target.value)}
              placeholder="When is your brain sharpest for your most important work?"
              className="glass-structured-form-input"
            />
          </div>
          <div>
            <label className="glass-form-label">Afternoon Block</label>
            <input
              type="text"
              value={formData.daily.deepWorkBlocks.afternoon}
              onChange={(e) => updateObjectField('daily', 'deepWorkBlocks', 'afternoon', e.target.value)}
              placeholder="What strategic thinking do you need uninterrupted time for?"
              className="glass-structured-form-input"
            />
          </div>
        </div>
      </div>

      {/* Quarterly Progress */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">QUARTERLY MOMENTUM</h3>
          <p className="glass-section-subtitle">90-day targets</p>
        </div>
        <div className="glass-space-y-2">
          {formData.daily.quarterlyProgress.map((objective, index) => (
            <div key={index} className="glass-flex glass-items-center glass-space-x-3">
              <span className="glass-goal-number">Q{index + 1}</span>
              <input
                type="text"
                value={objective}
                onChange={(e) => updateArrayField('daily', 'quarterlyProgress', index, e.target.value)}
                placeholder="What big goal needs your attention this quarter?"
                className="glass-structured-form-input flex-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Wins and Momentum */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">WINS</h3>
          <p className="glass-section-subtitle">Forward momentum</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.daily.wins}
          onChange={(e) => updateFormData('daily', 'wins', e.target.value)}
          placeholder="What progress did you make today that you can build on tomorrow?"
          className="glass-structured-form-textarea"
          rows="3"
        />
      </div>

      {/* Obstacles and Solutions */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">OBSTACLES</h3>
          <p className="glass-section-subtitle">Block & tackle</p>
        </div>
        <div className="glass-grid glass-grid-cols-1 glass-md-grid-cols-2">
          <div>
            <label className="glass-form-label">Block</label>
            <input
              type="text"
              value={formData.daily.obstacles.challenge}
              onChange={(e) => updateObjectField('daily', 'obstacles', 'challenge', e.target.value)}
              placeholder="What is the biggest thing slowing you down right now?"
              className="glass-structured-form-input"
            />
          </div>
          <div>
            <label className="glass-form-label">Solution</label>
            <input
              type="text"
              value={formData.daily.obstacles.solution}
              onChange={(e) => updateObjectField('daily', 'obstacles', 'solution', e.target.value)}
              placeholder="What is one step you can take to overcome this obstacle?"
              className="glass-structured-form-input"
            />
          </div>
        </div>
      </div>

      {/* Learning and Growth */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">LEARNING</h3>
          <p className="glass-section-subtitle">Knowledge capital</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.daily.learning}
          onChange={(e) => updateFormData('daily', 'learning', e.target.value)}
          placeholder="What did you learn today that will make you more effective going forward?"
          className="glass-structured-form-textarea"
          rows="3"
        />
      </div>

      {/* Tomorrow's Priorities */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">TOMORROW</h3>
          <p className="glass-section-subtitle">Pre-planned execution</p>
        </div>
        <div className="glass-space-y-2">
          {formData.daily.tomorrowPriorities.map((priority, index) => (
            <div key={index} className="glass-flex glass-items-center glass-space-x-3">
              <span className="glass-goal-number">{index + 1}.</span>
              <input
                type="text"
                value={priority}
                onChange={(e) => updateArrayField('daily', 'tomorrowPriorities', index, e.target.value)}
                placeholder="What is the most important thing you need to accomplish tomorrow?"
                className="glass-structured-form-input flex-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Reflection */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">REFLECTION</h3>
          <p className="glass-section-subtitle">Principles check</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.daily.reflection}
          onChange={(e) => updateFormData('daily', 'reflection', e.target.value)}
          placeholder="How did your choices today move you closer to who you want to become?"
          className="glass-structured-form-textarea"
          rows="3"
        />
      </div>
    </div>
  );

  const renderMeetingForm = () => (
    <div className="glass-space-y-6">
      {/* Attendees */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">WHO</h3>
          <p className="glass-section-subtitle">Key players</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.meeting.attendees}
          onChange={(e) => updateFormData('meeting', 'attendees', e.target.value)}
          placeholder="Who are the decision-makers and what do they each want?"
          className="glass-structured-form-textarea"
          rows="2"
        />
      </div>

      {/* Agenda */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">AGENDA</h3>
          <p className="glass-section-subtitle">What to cover</p>
        </div>
        <div className="glass-space-y-2">
          {formData.meeting.agenda.map((item, index) => (
            <div key={index} className="glass-flex glass-items-center glass-space-x-3">
              <span className="glass-goal-number">{index + 1}.</span>
              <input
                type="text"
                value={item}
                onChange={(e) => updateArrayField('meeting', 'agenda', index, e.target.value)}
                placeholder={`What decision or outcome needs this discussion?`}
                className="glass-structured-form-input flex-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Key Decisions */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">DECISIONS</h3>
          <p className="glass-section-subtitle">What was locked in</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.meeting.keyDecisions}
          onChange={(e) => updateFormData('meeting', 'keyDecisions', e.target.value)}
          placeholder="What concrete decisions were made and who committed to what?"
          className="glass-structured-form-textarea"
          rows="4"
        />
      </div>

      {/* Action Items */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">ACTIONS</h3>
          <p className="glass-section-subtitle">Who does what by when</p>
        </div>
        <div className="glass-space-y-2">
          {formData.meeting.actionItems.map((item, index) => (
            <div key={index} className="glass-flex glass-items-center glass-space-x-3">
              <span className="glass-goal-number">□</span>
              <input
                type="text"
                value={item}
                onChange={(e) => updateArrayField('meeting', 'actionItems', index, e.target.value)}
                placeholder={`Who will do what by when to move this forward?`}
                className="glass-structured-form-input flex-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Next Steps */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">NEXT</h3>
          <p className="glass-section-subtitle">Immediate follow-up</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.meeting.nextSteps}
          onChange={(e) => updateFormData('meeting', 'nextSteps', e.target.value)}
          placeholder="What happens immediately after this meeting to maintain momentum?"
          className="glass-structured-form-textarea"
          rows="3"
        />
      </div>

      {/* Strategic Insights */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">INSIGHTS</h3>
          <p className="glass-section-subtitle">Pattern recognition</p>
        </div>
        <TextareaWithLineNumbers
          value={formData.meeting.insights}
          onChange={(e) => updateFormData('meeting', 'insights', e.target.value)}
          placeholder="What patterns or opportunities emerged from this conversation?"
          className="glass-structured-form-textarea"
          rows="3"
        />
      </div>
    </div>
  );

  const renderProjectForm = () => (
    <div className="glass-space-y-6">
      {/* Project Overview */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">VISION</h3>
          <p className="glass-section-subtitle">What & why</p>
        </div>
        <div className="glass-space-y-4">
          <div>
            <label className="glass-form-label">Project Name</label>
            <input
              type="text"
              value={formData.project.projectName}
              onChange={(e) => updateFormData('project', 'projectName', e.target.value)}
              placeholder="What transformation are you creating?"
              className="glass-structured-form-input"
            />
          </div>
          <div>
            <label className="glass-form-label">Objective</label>
            <input
              type="text"
              value={formData.project.objective}
              onChange={(e) => updateFormData('project', 'objective', e.target.value)}
              placeholder="What problem does this solve and for whom?"
              className="glass-structured-form-input"
            />
          </div>
          <div>
            <label className="glass-form-label">Success Definition</label>
            <input
              type="text"
              value={formData.project.successDefinition}
              onChange={(e) => updateFormData('project', 'successDefinition', e.target.value)}
              placeholder="How will you know this project changed everything?"
              className="glass-structured-form-input"
            />
          </div>
        </div>
      </div>

      {/* Lean Startup Approach */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">BUILD-MEASURE-LEARN</h3>
          <p className="glass-section-subtitle">Lean cycle</p>
        </div>
        <div className="glass-space-y-4">
          <div className="glass-grid glass-grid-cols-1 glass-md-grid-cols-3">
            <div>
              <label className="glass-form-label">BUILD</label>
              <input
                type="text"
                value={formData.project.leanApproach.build}
                onChange={(e) => updateObjectField('project', 'leanApproach', 'build', e.target.value)}
                placeholder="What's the smallest version that proves value?"
                className="glass-structured-form-input"
              />
            </div>
            <div>
              <label className="glass-form-label">MEASURE</label>
              <input
                type="text"
                value={formData.project.leanApproach.measure}
                onChange={(e) => updateObjectField('project', 'leanApproach', 'measure', e.target.value)}
                placeholder="What metric tells you if this is working?"
                className="glass-structured-form-input"
              />
            </div>
            <div>
              <label className="glass-form-label">LEARN</label>
              <input
                type="text"
                value={formData.project.leanApproach.learn}
                onChange={(e) => updateObjectField('project', 'leanApproach', 'learn', e.target.value)}
                placeholder="What assumption are you testing first?"
                className="glass-structured-form-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">TIMELINE</h3>
          <p className="glass-section-subtitle">Phase gates</p>
        </div>
        <div className="glass-space-y-2">
          {['Discovery', 'Design', 'Build', 'Launch'].map((phase, index) => (
            <div key={index} className="glass-flex glass-items-center glass-space-x-3">
              <span className="glass-goal-number">{index + 1}.</span>
              <span className="w-20 text-sm text-white/60">{phase}:</span>
              <input
                type="text"
                value={formData.project.timeline[index]}
                onChange={(e) => updateArrayField('project', 'timeline', index, e.target.value)}
                placeholder={`What must be completed in ${phase.toLowerCase()} phase?`}
                className="glass-structured-form-input flex-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Resources */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">RESOURCES</h3>
          <p className="glass-section-subtitle">What you need</p>
        </div>
        <div className="glass-space-y-4">
          <div className="glass-grid glass-grid-cols-1 glass-md-grid-cols-3">
            <div>
              <label className="glass-form-label">Team</label>
              <input
                type="text"
                value={formData.project.resources.team}
                onChange={(e) => updateObjectField('project', 'resources', 'team', e.target.value)}
                placeholder="Who are the essential people to make this happen?"
                className="glass-structured-form-input"
              />
            </div>
            <div>
              <label className="glass-form-label">Budget</label>
              <input
                type="text"
                value={formData.project.resources.budget}
                onChange={(e) => updateObjectField('project', 'resources', 'budget', e.target.value)}
                placeholder="What investment will this require to succeed?"
                className="glass-structured-form-input"
              />
            </div>
            <div>
              <label className="glass-form-label">Tools</label>
              <input
                type="text"
                value={formData.project.resources.tools}
                onChange={(e) => updateObjectField('project', 'resources', 'tools', e.target.value)}
                placeholder="What tools are critical to execute effectively?"
                className="glass-structured-form-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Success Metrics */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">OKRs</h3>
          <p className="glass-section-subtitle">Objectives & Key Results</p>
        </div>
        <div className="glass-space-y-4">
          <div>
            <label className="glass-form-label">Objective</label>
            <input
              type="text"
              value={formData.project.metrics.objective}
              onChange={(e) => updateObjectField('project', 'metrics', 'objective', e.target.value)}
              placeholder="What's the overarching goal this project achieves?"
              className="glass-structured-form-input"
            />
          </div>
          <div className="glass-grid glass-grid-cols-1 glass-md-grid-cols-3 gap-4">
            {['kr1', 'kr2', 'kr3'].map((kr, index) => (
              <div key={kr}>
                <label className="glass-form-label">Key Result {index + 1}</label>
                <input
                  type="text"
                  value={formData.project.metrics[kr]}
                  onChange={(e) => updateObjectField('project', 'metrics', kr, e.target.value)}
                  placeholder={`What measurable outcome proves success?`}
                  className="glass-structured-form-input"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risk Mitigation */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">RISKS</h3>
          <p className="glass-section-subtitle">What could go wrong</p>
        </div>
        <div className="glass-space-y-2">
          {['Technical', 'Market', 'Resource'].map((riskType, index) => (
            <div key={index} className="glass-space-y-2">
              <label className="glass-form-label text-xs">{riskType}</label>
              <input
                type="text"
                value={formData.project.risks[index]}
                onChange={(e) => updateArrayField('project', 'risks', index, e.target.value)}
                placeholder={`What could derail this and how will you prevent it?`}
                className="glass-structured-form-input"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Stakeholder Alignment */}
      <div className="glass-form-section">
        <div className="glass-section-header">
          <h3 className="glass-section-title">WHY-HOW-WHAT</h3>
          <p className="glass-section-subtitle">Golden Circle</p>
        </div>
        <div className="glass-space-y-4">
          <div className="glass-grid glass-grid-cols-1 glass-md-grid-cols-3">
            <div>
              <label className="glass-form-label">WHY</label>
              <input
                type="text"
                value={formData.project.stakeholders.why}
                onChange={(e) => updateObjectField('project', 'stakeholders', 'why', e.target.value)}
                placeholder="Why does this project matter to the world?"
                className="glass-structured-form-input"
              />
            </div>
            <div>
              <label className="glass-form-label">HOW</label>
              <input
                type="text"
                value={formData.project.stakeholders.how}
                onChange={(e) => updateObjectField('project', 'stakeholders', 'how', e.target.value)}
                placeholder="How is your approach uniquely effective?"
                className="glass-structured-form-input"
              />
            </div>
            <div>
              <label className="glass-form-label">WHAT</label>
              <input
                type="text"
                value={formData.project.stakeholders.what}
                onChange={(e) => updateObjectField('project', 'stakeholders', 'what', e.target.value)}
                placeholder="What will you deliver that creates lasting impact?"
                className="glass-structured-form-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActiveForm = () => {
    switch (activeTab) {
      case 'daily':
        return renderDailyForm();
      case 'meeting':
        return renderMeetingForm();
      case 'project':
        return renderProjectForm();
      default:
        return renderDailyForm();
    }
  };

  return (
    <div className={`glass-journal-editor ${showAIChat ? 'with-chat' : ''}`}>
      {/* Header */}
      <div className="glass-editor-header">
        <div className="glass-header-top">
          <div className="flex items-center gap-2">
            <h2>Success Journal</h2>
            <button 
              onClick={() => setShowInfoModal(true)}
              className="glass-info-button"
              title="What is this journal for?"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
          
          {/* Compact Tab Dropdown */}
          <div className="glass-tab-dropdown-container">
            <button 
              onClick={() => setShowTabDropdown(!showTabDropdown)}
              className="glass-tab-dropdown-button"
            >
              <div className="glass-tab-dropdown-current">
                <div className="glass-tab-dropdown-icon">
                  {tabs.find(tab => tab.id === activeTab)?.icon}
                </div>
                <span>{tabs.find(tab => tab.id === activeTab)?.name}</span>
              </div>
              <span className="glass-tab-dropdown-arrow">
                {showTabDropdown ? '⌃' : '⌄'}
              </span>
            </button>
            
            {showTabDropdown && (
              <div className="glass-tab-dropdown-menu">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setShowTabDropdown(false);
                    }}
                    className={`glass-tab-dropdown-item ${activeTab === tab.id ? 'active' : ''}`}
                    data-tab={tab.id}
                  >
                    <div className="glass-tab-dropdown-icon">
                      {tab.icon}
                    </div>
                    <div className="glass-tab-dropdown-content">
                      <div className="glass-tab-dropdown-name">{tab.name}</div>
                      <div className="glass-tab-dropdown-desc">{tab.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button 
            onClick={handleBusinessCoachToggle}
            className="glass-ai-chat-toggle"
          >
            {showAIChat ? '× Close Coach' : 'Business Coach'}
          </button>
        </div>
        <div className="glass-date-header">
          <h3>{currentDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</h3>
        </div>
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div className="glass-info-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="glass-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-info-header">
              <h3>Your Success Journal</h3>
              <button 
                onClick={() => setShowInfoModal(false)}
                className="glass-info-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="glass-info-content">
              <div className="glass-info-section">
                <h4>🎯 What is this?</h4>
                <p>A daily system that helps you identify what's blocking your goals and take action to remove those blocks.</p>
              </div>
              
              <div className="glass-info-section">
                <h4>🚀 How it works:</h4>
                <div className="glass-info-tabs">
                  <div className="glass-info-tab">
                    <span className="glass-info-tab-name">Focus</span>
                    <p>Plan your day around what matters most. Identify obstacles early and solve them before they grow.</p>
                  </div>
                  <div className="glass-info-tab">
                    <span className="glass-info-tab-name">Align</span>
                    <p>Turn meetings into clear decisions and actions. No more confusion about "what happens next."</p>
                  </div>
                  <div className="glass-info-tab">
                    <span className="glass-info-tab-name">Build</span>
                    <p>Break big projects into small, testable steps. Learn fast, adjust quickly, and avoid costly mistakes.</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-info-section">
                <h4>📈 Why it works:</h4>
                <ul>
                  <li><strong>Daily Progress:</strong> Small consistent actions compound into big results</li>
                  <li><strong>Problem Solving:</strong> Spot obstacles early when they're still easy to fix</li>
                  <li><strong>Clear Thinking:</strong> Writing forces you to think through problems completely</li>
                  <li><strong>Track Momentum:</strong> See your wins pile up and patterns emerge</li>
                </ul>
              </div>
              
              <div className="glass-info-section">
                <h4>⚡ The Questions:</h4>
                <div className="glass-info-questions">
                  <div><strong>What's your ONE thing today?</strong> - Focus your energy</div>
                  <div><strong>What's blocking you?</strong> - Identify obstacles</div>
                  <div><strong>What's your solution?</strong> - Plan your attack</div>
                  <div><strong>What did you win?</strong> - Build momentum</div>
                  <div><strong>What did you learn?</strong> - Get smarter</div>
                </div>
              </div>
              
              <div className="glass-info-bottom">
                <p><strong>Bottom line:</strong> This isn't just writing about your day. It's actively engineering your success by solving problems before they stop you.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="glass-form-content">
        {renderActiveForm()}
      </div>

      {/* Save Section */}
      <div className="glass-save-section">
        <div className="glass-save-info">
          <div className="glass-entry-preview">
            <h4>{getEntryTitle()}</h4>
            <p>{getEntrySummary()}</p>
          </div>
          <button 
            onClick={handleSave}
            disabled={!isFormValid() || isSaving}
            className="glass-save-button"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StructuredJournalForms;